import { Button, ButtonProps } from '@components'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { useTheme } from '@hooks'
import { UploadOptions } from '@query'
import { AnimatePresence, motion } from 'framer-motion'
import React, { forwardRef } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { FaPaperclip } from 'react-icons/fa'
import { useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { uploadFileWithToast } from './uploadFileWithToast'

type FileUpload = (file: File, options?: UploadOptions) => Promise<any>

interface UploadFilesButtonProps extends ButtonProps {
  uploadFile: FileUpload
  fileTypes?: string[]
  enableAttachmentNotes?: boolean
}

export const useUpload = (
  uploadFile: FileUpload,
  noDrag = false,
  accept?: string[],
  disabled: boolean = false,
  enableAttachmentNotes = false
) => {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const hasFilesEditPermission = usePermission(PermissionSet.FILES_EDIT)
  const canEditNotes = enableAttachmentNotes && hasFilesEditPermission

  return useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: true,
    disabled: disabled,
    noDrag,
    accept,
    onDrop: newFiles =>
      newFiles.map(file =>
        uploadFileWithToast(file, options => uploadFile(file, options), {
          slug,
          canEditNotes,
          queryClient,
          theme
        })
      )
  })
}

export const UploadFilesButton = forwardRef<any, UploadFilesButtonProps>(
  ({ uploadFile, fileTypes, enableAttachmentNotes = false, children = false, ...rest }, ref) => {
    const { open, getRootProps, getInputProps } = useUpload(
      uploadFile,
      true,
      fileTypes,
      false,
      enableAttachmentNotes
    )

    return (
      <>
        <input {...getInputProps()} />
        <Button
          {...(getRootProps() as any as ButtonProps)}
          ref={ref}
          {...rest}
          onClick={() => open()}
        >
          {children}
        </Button>
      </>
    )
  }
)

export const UploadDropzone: React.FC<UploadFilesButtonProps> = ({
  uploadFile,
  fileTypes,
  enableAttachmentNotes = false,
  children,
  disabled = false
}) => {
  const { getRootProps, getInputProps, isDragActive } = useUpload(
    uploadFile,
    false,
    fileTypes,
    disabled,
    enableAttachmentNotes
  )
  const [t] = useTranslation()

  return (
    <StyledUploadDropzone {...getRootProps()}>
      <input {...getInputProps()} />
      {children}

      <AnimatePresence>
        {isDragActive && (
          <UploadOverlay>
            <UploadText>
              <FaPaperclip />
              <>{t('components.fileSelector.dropHere')}</>
            </UploadText>
          </UploadOverlay>
        )}
      </AnimatePresence>
    </StyledUploadDropzone>
  )
}

const StyledUploadDropzone = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`

const overlayVariants = {
  show: {
    opacity: 1
  },
  hide: {
    opacity: 0
  }
}

const textVariants = {
  show: {
    scale: 1,
    opacity: 1
  },
  hide: {
    scale: 0.9,
    opacity: 0
  }
}

const UploadOverlay = styled(motion.div).attrs({
  variants: overlayVariants,
  initial: 'hide',
  animate: 'show',
  exit: 'hide'
})`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border: 2px dashed ${({ theme }) => theme.colors.primary};
  background-color: ${({ theme }) => theme.colors.primary}22;
  z-index: 10;
  display: flex;
  justify-content: center;
  overflow: hidden;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const UploadText = styled(motion.div).attrs({ variants: textVariants })`
  align-self: center;
  color: white;
  font-weight: bold;
  border-radius: 0.6rem;
  padding: 0.3rem 0.7rem;
  background: white;
  background-color: ${({ theme }) => theme.colors.primary};
  display: flex;

  & > * {
    align-self: center;
  }

  svg {
    margin-right: 0.4rem;
  }
`
